import { ChangeOpacityComponents, ChangeOpacityMessage, ChangeOpacityMessagePrefix } from 'game-ui-types'
import { RefObject, useEffect, useRef, useState } from 'react'

import { getLastOpacityPayload } from './opacityMessageCache'

const useOpacityToggle = (
    componentKey: ChangeOpacityComponents,
    ref: RefObject<HTMLElement | SVGTextElement | null>,
    defaultOpacity = 1,
    defaultInteractivity = true
) => {
    const [cachedPayload] = useState(() =>
        defaultOpacity === 1 && defaultInteractivity ? getLastOpacityPayload(componentKey) : null
    )

    const [isComponentInteractive, setIsComponentInteractive] = useState(
        cachedPayload ? !!cachedPayload.isInteractive : defaultInteractivity
    )
    const [opacity, setOpacity] = useState(cachedPayload ? cachedPayload.opacity : defaultOpacity)
    const payloadRef = useRef<null | ChangeOpacityMessage['payload']>(cachedPayload)

    const changeOpacity = (payload: ChangeOpacityMessage['payload']) => {
        payloadRef.current = payload
        setIsComponentInteractive(!!payload.isInteractive)
        setOpacity(payload.opacity)
    }

    useEffect(() => {
        const handleMessage = (event: MessageEvent<ChangeOpacityMessage>) => {
            const rawData = event.data
            const type = rawData.type

            if (type === `${ChangeOpacityMessagePrefix}${componentKey}`) {
                const data = rawData as ChangeOpacityMessage

                changeOpacity(data.payload)
            }
        }

        window.addEventListener('message', handleMessage)

        return () => window.removeEventListener('message', handleMessage)
    }, [componentKey])

    useEffect(() => {
        if (!ref.current) return

        const { fade, duration } = payloadRef.current ?? {}

        if (fade) {
            ref.current.style.transition = 'opacity'
            ref.current.style.transitionDuration = duration ? `${duration}s` : '0.7s'
            ref.current.style.transitionTimingFunction = 'ease-in-out'
        } else {
            ref.current.style.transition = ''
        }

        ref.current.style.opacity = `${opacity}`
        // default to what ever pointer events set in Component
        ref.current.style.pointerEvents = isComponentInteractive ? '' : 'none'
        ;(ref.current as HTMLElement).inert = !isComponentInteractive
    }, [ref, opacity, isComponentInteractive])

    return {
        isComponentInteractive,
        opacity,
        changeOpacity,
    }
}

export default useOpacityToggle
